import React from 'react';
import { useAutocomplete } from '@/hooks/useAutocomplete';
import { getAutocompleteNames } from '../api';
import type { AutocompleteSuggestion } from '../types';

/**
 * Props for the useCustomerNameAutocomplete hook
 */
interface UseCustomerNameAutocompleteProps {
  /** The VIN value to use for searching. Must be exactly 17 characters to trigger suggestions. */
  vin: string;
  /** Callback function called when a user selects a suggestion from the dropdown */
  onSuggestionSelect: (customerName: string) => void;
  /** Debounce delay in milliseconds before making API calls. Defaults to 500ms. */
  debounceMs?: number;
  /** Function to validate VIN field - returns true if valid, false if invalid */
  isVinValid?: () => boolean;
}

/**
 * Custom hook for managing customer name autocomplete functionality in the contract search form.
 *
 * This hook provides a contract-search-specific autocomplete solution by combining the generic
 * useAutocomplete hook with VIN-based API calls and validation logic.
 *
 * **Features:**
 * - VIN-based customer name suggestions
 * - Integration with contract search API
 * - VIN validation integration
 * - All generic autocomplete functionality (keyboard navigation, focus management, etc.)
 *
 * **Usage Example:**
 * ```tsx
 * const autocomplete = useCustomerNameAutocomplete({
 *   vin: form.getFieldValue('vin') || '',
 *   onSuggestionSelect: (customerName) => {
 *     form.setFieldValue('customerName', customerName);
 *     form.validateField('customerName', 'change');
 *   },
 *   isVinValid: () => {
 *     const vinValue = form.getFieldValue('vin') || '';
 *     return vinSchema.safeParse(vinValue).success;
 *   },
 * });
 *
 * // Use in component:
 * <AutocompleteInput
 *   onChange={(value) => {
 *     field.handleChange(value);
 *     autocomplete.handleInputChange(value);
 *   }}
 *   onBlur={(e) => {
 *     field.handleBlur(e);
 *     autocomplete.handleInputBlur(e.target.value);
 *   }}
 *   onFocus={(e) => {
 *     autocomplete.handleInputFocus(e.target.value);
 *   }}
 *   onKeyDown={(e) => {
 *     autocomplete.handleKeyDown(e, e.currentTarget.value);
 *   }}
 *   suggestions={autocomplete.suggestions}
 *   isFetching={autocomplete.isFetching}
 *   focusedIndex={autocomplete.focusedIndex}
 *   onSuggestionClick={autocomplete.handleSuggestionClick}
 *   // ... other props
 * />
 * ```
 *
 * @param props - Configuration object for the autocomplete behavior
 * @returns Object containing suggestion state and event handlers
 */
export function useCustomerNameAutocomplete({
  vin,
  onSuggestionSelect,
  debounceMs = 500,
  isVinValid,
}: UseCustomerNameAutocompleteProps) {
  /**
   * Fetches filtered customer name suggestions based on VIN and input value
   */
  const fetchSuggestions = React.useCallback(
    async (inputValue: string): Promise<AutocompleteSuggestion[]> => {
      if (inputValue.trim() === '') {
        return [];
      }

      const response = await getAutocompleteNames({
        vin,
        query: inputValue,
      });

      return response.suggestions || [];
    },
    [vin]
  );

  /**
   * Fetches all customer name suggestions for the current VIN
   */
  const fetchAllSuggestions = React.useCallback(async (): Promise<
    AutocompleteSuggestion[]
  > => {
    const response = await getAutocompleteNames({
      vin,
      query: '', // Empty string to indicate "get all"
    });

    return response.suggestions || [];
  }, [vin]);

  /**
   * Wraps the user's onSuggestionSelect to extract the suggestion text
   */
  const handleSuggestionSelect = React.useCallback(
    (suggestion: AutocompleteSuggestion) => {
      onSuggestionSelect(suggestion.suggestion);
    },
    [onSuggestionSelect]
  );

  // Use the generic autocomplete hook with contract-search-specific configuration
  const autocomplete = useAutocomplete({
    fetchSuggestions,
    fetchAllSuggestions,
    onSuggestionSelect: handleSuggestionSelect,
    isValid: isVinValid,
    debounceMs,
  });

  return {
    /** Array of contract suggestions to display */
    suggestions: autocomplete.suggestions,
    /** Whether an API call is currently in progress */
    isFetching: autocomplete.isFetching,
    /** Index of the currently focused suggestion (-1 if none) */
    focusedIndex: autocomplete.focusedIndex,
    /** Handler for input value changes (includes debouncing logic) */
    handleInputChange: autocomplete.handleInputChange,
    /** Handler for input blur events (includes delay for click handling) */
    handleInputBlur: autocomplete.handleInputBlur,
    /** Handler for input focus events to reopen dropdown */
    handleInputFocus: autocomplete.handleInputFocus,
    /** Handler for keyboard navigation that doesn't block typing */
    handleKeyDown: autocomplete.handleKeyDown,
    /** Handler for suggestion clicks */
    handleSuggestionClick: autocomplete.handleSuggestionClick,
    /** Function to manually clear all suggestions */
    clearSuggestions: autocomplete.clearSuggestions,
    /** Function to fetch all customer suggestions for a VIN */
    fetchAllSuggestions: autocomplete.fetchAllSuggestions,
  };
}
